import React, { useState, useEffect, useCallback, useRef } from 'react';
import './VolumeBoost.css';
import { API_BASE_URL } from '../config';
import WatchlistStar from './WatchlistStar';

const VolumeBoost = React.memo(({ onBack }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const abortControllerRef = useRef(null);

  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    return num.toLocaleString('en-IN');
  };

  const formatPercentage = (value) => {
    if (!value) return '0.00%';
    if (typeof value === 'string') {
      // Handle case where value is already a percentage string (e.g., "20.00%" or "-11.72%")
      return value.endsWith('%') ? value : `${value}%`;
    }
    // Handle case where value is a number
    const num = parseFloat(value);
    return `${num > 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  const parseChangeValue = (changeString) => {
    if (!changeString) return 0;
    // Remove % sign and parse the number
    const numStr = changeString.toString().replace('%', '');
    return parseFloat(numStr) || 0;
  };

  const calculateVolumeChangePercent = (volume, avgVolume) => {
    if (!volume || !avgVolume || avgVolume === 0) return 0;
    // Ensure we're working with numbers
    const vol = parseFloat(volume);
    const avgVol = parseFloat(avgVolume);
    if (isNaN(vol) || isNaN(avgVol) || avgVol === 0) return 0;
    const changePercent = ((vol - avgVol) / avgVol) * 100;
    return changePercent;
  };

  const fetchVolumeBoostData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/volume-boost?limit=20`, {
        signal: controller.signal
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch volume boost data');
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setStocks(data.data);
        setLastUpdated(new Date());
        setError('');
      } else {
        throw new Error(data.message || 'Error fetching data');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching volume boost data:', err);
        setError('Failed to load volume boost data. ' + (err.message || 'Please try again later.'));
        setStocks([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVolumeBoostData();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchVolumeBoostData]);

  const handleRefresh = useCallback(() => {
    fetchVolumeBoostData();
  }, [fetchVolumeBoostData]);

  return (
    <div className="vb-page">
      <header className="intraday-header">
        <div className="intraday-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          <div className="intraday-user">
            <button onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>
      
      <main className="volume-boost-container">
        <div className="volume-boost-header">
          <h1>Volume Boost Stocks</h1>
          <div className="volume-boost-controls">
            <button onClick={handleRefresh} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            {lastUpdated && (
              <div className="last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
        
        {loading && !stocks.length ? (
          <div className="loading">Loading volume boost data...</div>
        ) : error ? (
          <div className="error">
            {error}
            <button onClick={handleRefresh} className="retry-button">
              Retry
            </button>
          </div>
        ) : (
          <div className="stocks-table">
            <div className="table-header">
              <div>Symbol</div>
              <div>LTP</div>
              <div>Change</div>
              <div>Volume</div>
              <div>Avg Volume</div>
              <div>Volume Change %</div>
              <div>Timestamp</div>
            </div>
            
            {stocks.map((stock) => (
              <StockRow 
                key={stock.symbol} 
                stock={stock}
                formatNumber={formatNumber}
                formatPercentage={formatPercentage}
                parseChangeValue={parseChangeValue}
                calculateVolumeChangePercent={calculateVolumeChangePercent}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
});

const StockRow = React.memo(({ stock, formatNumber, formatPercentage, parseChangeValue, calculateVolumeChangePercent }) => {
  const changeValue = parseChangeValue(stock.change);
  const isPositive = changeValue > 0;
  const isNegative = changeValue < 0;
  const volumeChangePercent = calculateVolumeChangePercent(stock.volume, stock.avg_volume);
  const isVolumePositive = volumeChangePercent > 0;
  const isVolumeNegative = volumeChangePercent < 0;

  return (
    <div className={`stock-row ${isPositive ? 'positive' : isNegative ? 'negative' : ''}`}>
      <div className="stock-symbol">
        <WatchlistStar 
          stock={{
            symbol: stock.symbol,
            name: stock.symbol
          }}
        />
        {stock.symbol || 'N/A'}
      </div>
      <div className="stock-ltp">{formatNumber(stock.ltp)}</div>
      <div className={`stock-change ${isPositive ? 'positive' : isNegative ? 'negative' : ''}`}>
        {formatPercentage(stock.change)}
      </div>
      <div className="stock-volume">{formatNumber(stock.volume)}</div>
      <div className="stock-avg-volume">{formatNumber(stock.avg_volume)}</div>
      <div className={`stock-volume-change ${isVolumePositive ? 'positive' : isVolumeNegative ? 'negative' : ''}`}>
        {formatPercentage(volumeChangePercent)}
      </div>
      <div className="stock-timestamp">
        {stock.timestamp ? new Date(stock.timestamp).toLocaleTimeString() : 'N/A'}
      </div>
    </div>
  );
});

export default VolumeBoost;